// StepCard.js
import React from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

export default function EvaluationCard({
  id,
  title,
  status,
  statusLabel,
  icon,
  unlocked,
  prevValidated,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLocked = !(unlocked && prevValidated);
  const hideStatusWhenLocked = isLocked && status === "not_started";

  // determine icon color based on status
  const iconColor =
    status === "completed"
      ? theme.palette.success.main
      : status === "pending" || status === "in_progress"
      ? theme.palette.warning.main
      : status === "failed"
      ? theme.palette.error.main
      : unlocked
      ? theme.palette.primary.main
      : theme.palette.text.secondary;

  // clone passed icon and apply color + responsive size
  const coloredIcon = React.isValidElement(icon)
    ? React.cloneElement(icon, {
        sx: {
          color: iconColor,
          fontSize: { xs: 22, sm: 24 },
          ...(icon.props?.sx || {}),
        },
      })
    : icon;

  return (
    <Card
      sx={(theme) => {
        // border color based on status; keep default background
        const borderColor =
          status === "completed"
            ? theme.palette.success.main
            : status === "pending"
            ? theme.palette.warning.main
            : status === "failed"
            ? theme.palette.error.main
            : "#ddd";

        return {
          borderRadius: { xs: 3, sm: 4 },
          boxShadow: unlocked
            ? "0 6px 16px rgba(15, 23, 42, 0.08)"
            : "0 3px 8px rgba(15, 23, 42, 0.06)",
          border: `1px solid ${alpha(borderColor, 0.5)}`,
          background: theme.palette.background.paper,
          height: "auto",
          minHeight: { xs: 124, sm: 116 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          overflow: "visible",
          p: { xs: 1.15, sm: 1.1 },
          position: "relative",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: unlocked ? "translateY(-1px)" : "none",
            boxShadow: "0 9px 18px rgba(15, 23, 42, 0.1)",
          },
        };
      }}
    >
      <CardContent sx={{ flexGrow: 0, pb: { xs: 0.85, sm: 0.6 } }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1.1}
          sx={{ "& svg": { fontSize: { xs: 22, sm: 24 } }, flexWrap: "nowrap" }}
        >
          {coloredIcon}
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              color: iconColor,
              fontSize: { xs: "1.14rem", sm: "1.12rem" },
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {id}. {title}
          </Typography>
        </Box>

        {!hideStatusWhenLocked && (
          <Box display="flex" alignItems="center" sx={{ mt: 1.45, minHeight: 22 }}>
            <Box
              sx={{
                width: 20,
                mr: 0.9,
                display: "flex",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {status === "completed" ? (
                <CheckCircleIcon color="success" sx={{ fontSize: { xs: 19, sm: 18 } }} />
              ) : null}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: status === "completed" ? "success.dark" : iconColor,
                fontSize: { xs: "0.95rem", sm: "0.88rem" },
                fontWeight: status === "completed" ? 600 : 500,
                lineHeight: 1.15,
              }}
            >
              {statusLabel || t(status)}
            </Typography>
          </Box>
        )}
      </CardContent>

      <Box sx={{ mt: 0.95 }}>
        {isLocked ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            color="text.secondary"
            sx={{
              pb: 0,
              minHeight: 22,
              pl: { xs: 3.3, sm: 3.5 },
            }}
          >
            <LockIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.8 }} />
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.9rem", sm: "0.9rem" }, lineHeight: 1.1 }}
            >
              Locked
            </Typography>
          </Box>
        ) : (
          <Box sx={{ minHeight: 8 }} />
        )}
      </Box>
    </Card>
  );
}
